import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Paintbrush, Upload, Loader2, Coins } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DesignGenerator() {
  const [patterns, setPatterns] = useState([]);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [myDesigns, setMyDesigns] = useState([]);
  const [designForm, setDesignForm] = useState({ sport: 'football', primary_color: '#1a1a8e', secondary_color: '#ffffff', pattern_id: '' });
  const [clientLogoFile, setClientLogoFile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/design/patterns`, { withCredentials: true }),
      axios.get(`${API}/api/design/tokens`, { withCredentials: true }),
      axios.get(`${API}/api/design/my-designs`, { withCredentials: true }),
    ]).then(([pRes, tRes, dRes]) => {
      setPatterns(pRes.data);
      setTokenInfo(tRes.data);
      setMyDesigns(dRes.data);
      if (pRes.data.length > 0) setDesignForm(prev => ({ ...prev, pattern_id: pRes.data[0].id }));
    }).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!designForm.pattern_id) { toast.error('Sélectionnez un pattern'); return; }
    setGenerating(true);
    setGeneratedResult(null);
    try {
      const fd = new FormData();
      fd.append('primary_color', designForm.primary_color);
      fd.append('secondary_color', designForm.secondary_color);
      fd.append('pattern_id', designForm.pattern_id);
      fd.append('sport', designForm.sport);
      if (clientLogoFile) fd.append('client_logo', clientLogoFile);
      const res = await axios.post(`${API}/api/design/generate`, fd, { withCredentials: true });
      setGeneratedResult(res.data);
      setTokenInfo(prev => prev ? { ...prev, tokens: res.data.tokens, generations: res.data.generations } : prev);
      setMyDesigns(prev => [{ image_url: res.data.image_url, primary_color: designForm.primary_color, secondary_color: designForm.secondary_color, pattern_id: designForm.pattern_id, created_at: new Date().toISOString() }, ...prev]);
      toast.success('Design généré !');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de génération');
    } finally { setGenerating(false); }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Génération IA — Designer Maillots</h1>
          {tokenInfo && (
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-2">
              <Coins size={18} className="text-amber-500" />
              <span className="text-sm font-bold text-slate-900">{tokenInfo.tokens}</span>
              <span className="text-xs text-slate-500">tokens</span>
              <span className="text-xs text-slate-400">|</span>
              <span className="text-xs text-slate-500">{tokenInfo.generations} généré(s)</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card className="border-slate-200" data-testid="designer-form">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Paintbrush size={18} className="text-slate-600" /> Créer un design
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-xs font-medium mb-2 block">Sport</Label>
                <div className="flex gap-2 flex-wrap">
                  {['football', 'rugby', 'handball', 'basketball', 'volleyball', 'running', 'cyclisme'].map(s => (
                    <button key={s} onClick={() => setDesignForm(p => ({...p, sport: s}))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${designForm.sport === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-2 block">Couleur principale</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={designForm.primary_color} onChange={e => setDesignForm(p => ({...p, primary_color: e.target.value}))} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" />
                    <Input value={designForm.primary_color} onChange={e => setDesignForm(p => ({...p, primary_color: e.target.value}))} className="h-9 text-xs font-mono flex-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium mb-2 block">Couleur secondaire</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={designForm.secondary_color} onChange={e => setDesignForm(p => ({...p, secondary_color: e.target.value}))} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" />
                    <Input value={designForm.secondary_color} onChange={e => setDesignForm(p => ({...p, secondary_color: e.target.value}))} className="h-9 text-xs font-mono flex-1" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium mb-2 block">Pattern</Label>
                <div className="grid grid-cols-3 gap-3">
                  {patterns.map(p => (
                    <button key={p.id} onClick={() => setDesignForm(prev => ({...prev, pattern_id: p.id}))}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${designForm.pattern_id === p.id ? 'border-slate-900 ring-2 ring-slate-300' : 'border-slate-200 hover:border-slate-400'}`}>
                      {p.thumbnail && <img src={p.thumbnail} alt={p.name} className="w-full h-20 object-cover" />}
                      <span className="block text-[10px] text-center py-1 bg-slate-50 text-slate-600">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium mb-2 block">Logo client (optionnel)</Label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-slate-300 hover:border-slate-400 cursor-pointer text-sm text-slate-600">
                    <Upload size={16} /> {clientLogoFile ? clientLogoFile.name : 'Choisir un fichier'}
                    <input type="file" accept="image/*" className="hidden" onChange={e => setClientLogoFile(e.target.files?.[0] || null)} />
                  </label>
                  {clientLogoFile && <button onClick={() => setClientLogoFile(null)} className="text-xs text-red-500 hover:underline">Supprimer</button>}
                </div>
              </div>
              <Button data-testid="generate-design-btn" onClick={handleGenerate} disabled={generating || !designForm.pattern_id || (tokenInfo && tokenInfo.tokens < 5)}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-2 py-5">
                {generating ? <><Loader2 size={16} className="animate-spin" /> Génération en cours...</> : <><Paintbrush size={16} /> Générer le design</>}
              </Button>
              {tokenInfo && tokenInfo.tokens < 5 && (
                <p className="text-xs text-red-500 text-center">Tokens insuffisants. Demandez à l'administrateur d'en ajouter.</p>
              )}
            </CardContent>
          </Card>

          <div className="space-y-4">
            {/* Loading animation during generation */}
            {generating && (
              <Card className="border-blue-200 bg-blue-50/30">
                <CardContent className="py-12 flex flex-col items-center justify-center">
                  <div className="relative w-24 h-24 mb-4">
                    <div className="absolute inset-0 rounded-full border-4 border-slate-200" />
                    <div className="absolute inset-0 rounded-full border-4 border-t-blue-600 animate-spin" />
                    <Paintbrush size={28} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-600" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">Design en cours de création...</p>
                  <p className="text-xs text-slate-500 mt-1">Cela peut prendre jusqu'à 1 minute</p>
                  <div className="mt-4 flex gap-1">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Generated result */}
            {!generating && generatedResult && (
              <Card className="border-emerald-200" data-testid="design-result">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-700">Design généré</CardTitle></CardHeader>
                <CardContent><img src={generatedResult.image_url} alt="Jersey design" className="w-full rounded-lg shadow-md" /></CardContent>
              </Card>
            )}

            {/* My designs with date/time */}
            {myDesigns.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Mes designs générés</CardTitle>
                    <span className="text-[10px] text-slate-400">Supprimés automatiquement après 48h</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {myDesigns.map((d, i) => (
                      <div key={i} className="rounded-lg overflow-hidden border border-slate-200 hover:shadow-md transition-shadow">
                        <img src={d.image_url} alt={`Design ${i + 1}`} className="w-full object-cover" />
                        <div className="p-2 bg-slate-50">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-3.5 h-3.5 rounded-full border" style={{ background: d.primary_color }} />
                            <div className="w-3.5 h-3.5 rounded-full border" style={{ background: d.secondary_color }} />
                            <Badge className="text-[9px] bg-slate-100 text-slate-500 border-0 ml-auto">{d.pattern_id}</Badge>
                          </div>
                          <p className="text-[10px] text-slate-400">
                            {d.created_at ? new Date(d.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                            {' '}
                            {d.created_at ? new Date(d.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
